/**
 * Themes component - Toggle between light and dark with two IconButtons
 */

import { createIconButton, type IconButtonAPI } from "@UI/IconButton/index.js";
import type { Theme } from "@/types/user-types.js";
import "./Themes.scss";

export interface ThemesConfig {
  readonly selected?: Theme;
  readonly onSelectionChange?: (theme: Theme) => void;
}

export interface ThemesAPI {
  readonly getElement: () => HTMLElement;
  readonly setSelected: (theme: Theme) => void;
  readonly getSelected: () => Theme | null;
  readonly destroy: () => void;
}

/**
 * Creates a Themes component with mutual exclusive selection between light and dark
 */
export const createThemes = (config: ThemesConfig = {}): ThemesAPI => {
  const { selected = "light", onSelectionChange } = config;

  let currentSelected: Theme | null = selected;
  let isDestroyed = false;

  // Button instances
  let lightButton: IconButtonAPI | null = null;
  let darkButton: IconButtonAPI | null = null;

  // Container
  const container = document.createElement("div");
  container.className = "themes";

  const updateSelection = (): void => {
    if (isDestroyed) return;
    lightButton?.setSelected(currentSelected === "light");
    darkButton?.setSelected(currentSelected === "dark");
  };

  const handleClick = (next: Theme): void => {
    if (isDestroyed || next === currentSelected) return;
    currentSelected = next;
    updateSelection();
    onSelectionChange?.(next);
  };

  const initButtons = (): void => {
    try {
      lightButton = createIconButton({
        icon: "themes_light",
        size: 30,
        iconSize: 28,
        behavior: "switch",
        selected: currentSelected === "light",
        className: "themes__button themes__button--light",
        onClick: () => handleClick("light"),
      });
      container.appendChild(lightButton.getElement());

      darkButton = createIconButton({
        icon: "themes_dark",
        size: 30,
        iconSize: 28,
        behavior: "switch",
        selected: currentSelected === "dark",
        className: "themes__button themes__button--dark",
        onClick: () => handleClick("dark"),
      });
      container.appendChild(darkButton.getElement());
    } catch (error) {
      console.error("Failed to create Themes buttons:", error);
    }
  };

  // Initialize
  initButtons();

  const api: ThemesAPI = {
    getElement: () => container,
    setSelected: (theme: Theme) => {
      if (isDestroyed) return;
      currentSelected = theme;
      updateSelection();
    },
    getSelected: () => currentSelected,
    destroy: () => {
      if (isDestroyed) return;
      isDestroyed = true;
      lightButton?.destroy();
      darkButton?.destroy();
      lightButton = null;
      darkButton = null;
      container.remove();
    },
  };

  return api;
};

export const createThemesInContainer = (
  container: HTMLElement,
  config: ThemesConfig
): ThemesAPI => {
  const themes = createThemes(config);
  container.appendChild(themes.getElement());
  return themes;
};
